import { IconButton, MenuItem, Select } from '@material-ui/core'
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown'
import ClearOutlinedIcon from '@material-ui/icons/ClearOutlined'
import DeleteIcon from '@material-ui/icons/Remove'
import React, { useMemo, useState } from 'react'

import AutocompleteForFilter from '../../components/AutoCompleteForFilter'
import CustomSelectCurrency from '../../components/CustomSelectCurrency/CustomSelectCurrency'
import {
  addCommaToPrice,
  convertPersianNumbersToEnglishNumbers,
  removeCommaFromPrice
} from '../../helpers/numbers'
import { getCurrenciesAvailable } from '../../helpers/userData'
import { getTranslate } from '../../localization'
import { Multiplier } from '../../types/bulkUpdate'
import { CancelPolicy, ModifyPolicy } from '../../types/cancelAndModifyPolicies'
import { Currency } from '../../types/currency'
import useStyles from './styles/InventoryInputItem.styles'

interface InventoryInputItemProps {
  label: string
  showCurrencySelector?: boolean
  value?: string
  onChange?: (value: string) => void
  onChangeCurrency?: (value: Currency) => void
  titleEditable?: boolean
  onChangeLabel?: (value: string) => void
  deletable?: boolean
  onDelete?: () => void
  isChannelSelected?: string
  cancelPolicyData?:
    | {
        cancelPolicies: CancelPolicy[]
      }
    | undefined
  cancelPolicyValue?: {
    label: string
    value: string
  } | null
  setCancelPolicyValue?: React.Dispatch<any>
  modifyPolicyValue?: {
    label: string
    value: string
  } | null
  setModifyPolicyValue?: React.Dispatch<any>
  modifyPolicyData?:
    | {
        modifyPolicies: ModifyPolicy[]
      }
    | undefined
  loadingForCancelPolicy?: boolean
  loadingForModifyPolicy?: boolean
  icon?: JSX.Element
  multiplierIndex?: number
  rateMultipliers?: Multiplier[]
  setRateMultipliers?: React.Dispatch<React.SetStateAction<Multiplier[]>>
  forCurrency?: boolean
  outputCurrency?: boolean
}

const InventoryInputItem: React.FC<InventoryInputItemProps> = ({
  label,
  showCurrencySelector,
  value,
  onChangeCurrency,
  titleEditable,
  deletable,
  onChangeLabel,
  onDelete,
  isChannelSelected,
  onChange,
  cancelPolicyData,
  cancelPolicyValue,
  setCancelPolicyValue,
  modifyPolicyValue,
  setModifyPolicyValue,
  loadingForCancelPolicy,
  modifyPolicyData,
  loadingForModifyPolicy,
  icon,
  multiplierIndex,
  rateMultipliers,
  setRateMultipliers,
  forCurrency,
  outputCurrency
}) => {
  const classes = useStyles()
  const [growIn, setgrowIn] = useState(false)
  const [listIsOpen, setlistIsOpen] = useState(false)
  const [modalForMobile, setmodalForMobile] = useState<boolean>(false)

  const valueWithComma = useMemo(() => addCommaToPrice(value || ''), [value])
  const handleChange = (newValue: string) => {
    newValue = convertPersianNumbersToEnglishNumbers(newValue)
    newValue = removeCommaFromPrice(newValue)

    if (!rateMultipliers) {
      onChange?.(newValue)
    } else {
      setRateMultipliers!([
        ...rateMultipliers!.slice(0, multiplierIndex),
        {
          ...rateMultipliers![multiplierIndex!],
          rateMultiplier: newValue
        },
        ...rateMultipliers!.slice(multiplierIndex! + 1)
      ])
    }
  }

  return (
    <div className={classes.root}>
      <div
        className={[
          classes.titleContainer,
          !!value ? classes.titleContainerAfterSetValue : '',
          rateMultipliers &&
          rateMultipliers.length > 0 &&
          rateMultipliers![multiplierIndex!].rateMultiplier
            ? classes.titleContainerAfterSetValue
            : ''
        ].join(' ')}>
        {!titleEditable ? (
          <div className={[icon ? classes.iconInfo : ''].join(' ')}>
            {label}
            {icon}
          </div>
        ) : (
          <input
            className={classes.titleInput}
            value={label}
            onChange={(e) => onChangeLabel!(e.currentTarget.value)}
          />
        )}
        {deletable && (
          <IconButton
            onClick={onDelete}
            className={classes.deleteIconContainer}>
            <DeleteIcon className={classes.deleteIcon} />
          </IconButton>
        )}
      </div>
      <div className={classes.valueContainer}>
        <input
          className={[classes.input, forCurrency && classes.inputCenter].join(
            ' '
          )}
          placeholder={
            icon
              ? ''
              : forCurrency
              ? getTranslate('Choose Currency')
              : isChannelSelected !== 'CRS'
              ? getTranslate('Write in here...')
              : !loadingForCancelPolicy && !loadingForModifyPolicy
              ? getTranslate('Choose a Policy')
              : getTranslate('Wait to load options...')
          }
          value={
            isChannelSelected === 'CRS' && cancelPolicyValue
              ? cancelPolicyValue?.label
              : modifyPolicyValue
              ? modifyPolicyValue?.label
              : rateMultipliers && !forCurrency
              ? rateMultipliers![multiplierIndex!].rateMultiplier
              : valueWithComma
          }
          onClick={() => {
            setgrowIn(true)
            setmodalForMobile(true)
          }}
          onChange={(e) => {
            handleChange(e.currentTarget.value)
          }}
        />
        {forCurrency ? (
          <div>
            <Select
              value={
                !outputCurrency
                  ? rateMultipliers![multiplierIndex!].inputCurrency
                  : rateMultipliers![multiplierIndex!].outputCurrency
              }
              onChange={(event) => {
                setRateMultipliers!([
                  ...rateMultipliers!.slice(0, multiplierIndex),
                  {
                    ...rateMultipliers![multiplierIndex!],
                    inputCurrency: !outputCurrency
                      ? (event.target.value as Currency)
                      : rateMultipliers![multiplierIndex!].inputCurrency,
                    outputCurrency: outputCurrency
                      ? (event.target.value as Currency)
                      : rateMultipliers![multiplierIndex!].outputCurrency
                  },
                  ...rateMultipliers!.slice(multiplierIndex! + 1)
                ])
              }}>
              {getCurrenciesAvailable().map((currency) => (
                <MenuItem
                  key={currency}
                  value={currency}
                  className={classes.menuItemRight}>
                  {currency}
                </MenuItem>
              ))}
            </Select>
          </div>
        ) : showCurrencySelector ? (
          <div className={classes.currencySelectorContainer}>
            <CustomSelectCurrency onChange={onChangeCurrency!} />
          </div>
        ) : (
          <ClearOutlinedIcon
            className={[
              classes.clearIcon,
              cancelPolicyValue && cancelPolicyValue.value.length > 1
                ? classes.clearIconShow
                : modifyPolicyValue && modifyPolicyValue.value.length > 1
                ? classes.clearIconShow
                : ''
            ].join(' ')}
            onClick={() => {
              if (cancelPolicyValue && cancelPolicyValue.value.length > 1) {
                setCancelPolicyValue?.(null)
              } else if (
                modifyPolicyValue &&
                modifyPolicyValue.value.length > 1
              ) {
                setModifyPolicyValue?.(null)
              }
            }}
          />
        )}
        {isChannelSelected === 'CRS' &&
          !loadingForCancelPolicy &&
          !loadingForModifyPolicy && (
            <AutocompleteForFilter
              noBorder
              fixPositionForBulkUpdate
              loading={false}
              hidePlacehoder
              Icon={<ArrowDropDownIcon />}
              options={
                cancelPolicyData
                  ? cancelPolicyData?.cancelPolicies.map((item) => ({
                      label: item.basicDetails.name || '',
                      value: item.basicDetails.id || ''
                    }))
                  : modifyPolicyData
                  ? modifyPolicyData?.modifyPolicies.map((item) => ({
                      label: item.basicDetails.name || '',
                      value: item.basicDetails.id || ''
                    }))
                  : []
              }
              value={
                cancelPolicyValue
                  ? [
                      {
                        label: cancelPolicyValue.label,
                        value: cancelPolicyValue.value
                      }
                    ]
                  : modifyPolicyValue
                  ? [
                      {
                        label: modifyPolicyValue.label,
                        value: modifyPolicyValue.value
                      }
                    ]
                  : [
                      {
                        label: '',
                        value: ''
                      }
                    ]
              }
              onClick={(e) => {
                if (cancelPolicyData) {
                  setCancelPolicyValue?.(e)
                } else {
                  setModifyPolicyValue?.(e)
                }
              }}
              setmodalForMobileIsOpen={setmodalForMobile}
              setlistIsOpen={setlistIsOpen}
              listIsOpen={listIsOpen}
              setgrowIn={setgrowIn}
              growIn={growIn}
              modalForMobile={modalForMobile}
            />
          )}
      </div>
    </div>
  )
}

export default InventoryInputItem
